#!/usr/bin/env node

import yargs from 'yargs'

import { loginCommand } from './commands/login/login'
import { deployCommand } from './commands/deploy/deploy'
import { syncCommand } from './commands/sync/syncCommand'
import { devCommand } from './commands/devMode/devMode'
import { buildCommand } from './commands/build/build'
import { initCommand } from './commands/login/init'
import { ApiError } from './apiError'

const checkNodeVersion = () => {
  const nodeVersion = process.version
  const versionParts = nodeVersion.slice(1).split('.')
  const majorVersion = parseInt(versionParts[0])
  if (majorVersion < 20) {
    console.error(`Error: Node v20 or later is required. Running node ${nodeVersion}`)
    process.exit(1)
  }
}

const addInstanceOptions = (yargs: yargs.Argv, prefix: string, description: string) => {
  const instanceUrl = `${prefix}url`
  const accessToken = `${prefix}access-token`
  const pathToUnixSocket = `${prefix}path-to-unix-socket`
  
  return yargs
    .string(instanceUrl)
    .describe(instanceUrl, `URL of the ${description} Retool instance. Must be used with --${accessToken}.`)
    .string(accessToken)
    .describe(accessToken, `Access token for the ${description} Retool instance. Must be used with --${instanceUrl}.`)
    .string(pathToUnixSocket)
    .describe(pathToUnixSocket, `Path to the unix socket for the ${description} Retool instance. Must be used with --${instanceUrl} and --${accessToken}.`)
    .check((argv) => {
      const hasUrl = argv[instanceUrl] !== undefined
      const hasToken = argv[accessToken] !== undefined
      const hasUnixSocket = argv[pathToUnixSocket] !== undefined

      if (hasUrl && !hasToken) {
        throw new Error(`--${instanceUrl} requires --${accessToken} to be specified too`)
      }
      if (hasToken && !hasUrl) {
        throw new Error(`--${accessToken} requires --${instanceUrl} to be specified too`)
      }
      if (hasUnixSocket && (!hasUrl || !hasToken)) {
        throw new Error(`--${pathToUnixSocket} requires --${instanceUrl} and --${accessToken} to be specified too`)
      }
      return true
    })
}

const withDevInstanceOptions = (yargs: yargs.Argv) => addInstanceOptions(yargs, '', 'development')
const withSyncTargetInstanceOptions = (yargs: yargs.Argv) => addInstanceOptions(yargs, 'target-', 'sync target')

checkNodeVersion()

yargs(process.argv.slice(2))
  /** This will be needed once deploy takes a version argument (it unreserves the word version)
   * .version(false)
   */
  .env('RETOOL_CCL')
  .command(
    'deploy',
    'Builds, and uploads a new version of the custom component library to Retool.',
    (yargs) => {
      return withDevInstanceOptions(yargs)
        .boolean('dry-run')
        .alias('dry-run', ['n'])
        .describe('dry-run', 'Builds the custom components locally but does not send them to Retool.')
        .boolean('verbose')
        .alias('verbose', ['v'])
        .describe('verbose', 'Print detailed error information including status codes, response bodies, and stack traces.')
    },
    deployCommand,
  )
  .command(
    'login',
    'Logs in to a Retool instance for further commands',
    (yargs) => {
      return yargs
        .boolean('status')
        .alias('status', ['s'])
        .describe('status', 'Shows if you are currently logged in to an instance, and if so which one')
        .boolean('unix-socket')
        .describe(
          'unix-socket',
          'Allows you to connect to a Retool instance via unix socket. The command will ask you to provide the socket, you do not need to provide it as part of this argument.',
        )
    },
    loginCommand,
  )
  .command(
    'sync',
    'Syncs custom component libraries from one Retool instance to another.',
    (yargs) => {
      return withDevInstanceOptions(withSyncTargetInstanceOptions(yargs))
        .boolean('unix-socket')
        .describe(
          'unix-socket',
          'Allows you to connect to a Retool instance via unix socket. The command will ask you to provide the socket, you do not need to provide it as part of this argument.',
        )
        .array('target-header')
        .describe(
          'target-header',
          'HTTP headers to be used when making HTTP requests to the target Retool instance. Each header should be a single string of the form --target-header "name: value".'
        )
        .boolean('verbose')
        .alias('verbose', ['v'])
        .describe('verbose', 'Print detailed error information including status codes, response bodies, and stack traces.')
    },
    syncCommand,
  )
  .command(
    'dev',
    'Starts dev mode, where updates will be automatically recompiled and sent to Retool in real time.',
    (yargs) => {
      return yargs
        .boolean('dry-run')
        .alias('dry-run', ['n'])
        .describe('dry-run', 'Builds the custom components locally but does not send them to Retool.')
    },
    devCommand,
  )
  .command(
    'init',
    'Creates a new custom component library in this package. Does so by writing some files locally, and also calling the Retool backend API.',
    () => { },
    initCommand,
  )
  .command(
    'build',
    'Builds the custom components locally, but does not upload them to Retool.',
    (yargs) => {
      return yargs
        .boolean('dev')
        .alias('dev', ['d'])
        .describe(
          'dev',
          'Builds the custom components locally in dev mode. This is the same build that would happen if the dev command was run.',
        )
    },
    buildCommand,
  )
  .option('skip-updates-check', {
    describe: 'Skips any attempts to check for updates to the @tryretool/custom-component-support npm library.',
    type: 'boolean'
  })
  .option('header', {
    describe: 'HTTP headers to be added to requests to the Retool instance. Each header should be a single string of the form --header "name: value".',
    type: 'array'
  })
  .fail(async function (msg, err, yargs) {
    // Check for verbose flag in process.argv as a fallback since yargs.argv might not be available
    const isVerbose = process.argv.includes('--verbose') || process.argv.includes('-v')
    
    if (err instanceof ApiError) {
      console.error(
        `Error happened while talking to the Retool backend: \n - ${err.errorMessage}${err.statusCode ? '\n - The status code was ' + err.statusCode + '.' : ''
        }`,
      )
      
      if (isVerbose) {
        console.error('\n--- Verbose Error Details ---')
        if (err.response) {
          console.error('Response URL:', err.response.url)
          console.error('Response Status:', err.response.status)
          console.error('Response Status Text:', err.response.statusText)
          console.error('Response Headers:', Object.fromEntries(err.response.headers.entries()))
          
          // Try to get response body
          try {
            const responseClone = err.response.clone()
            const responseText = await responseClone.text()
            console.error('Response Body:', responseText)
            
            // Try to parse as JSON for better formatting
            try {
              const jsonBody = JSON.parse(responseText)
              console.error('Response Body (JSON):', JSON.stringify(jsonBody, null, 2))
            } catch {
              // Already logged as text above
            }
          } catch (bodyError) {
            console.error('Could not read response body:', bodyError)
          }
        }
        if (err.stack) {
          console.error('Stack Trace:', err.stack)
        }
        console.error('Full Error Object:', err)
        console.error('--- End Verbose Details ---\n')
      }
    } else {
      console.error('Unhandled error:', {
        err, msg
      })
      
      if (isVerbose && err instanceof Error) {
        console.error('\n--- Verbose Error Details ---')
        console.error('Error Name:', err.name)
        console.error('Error Message:', err.message)
        if (err.stack) {
          console.error('Stack Trace:', err.stack)
        }
        console.error('Full Error Object:', err)
        console.error('--- End Verbose Details ---\n')
      }
    }
    process.exit(1)
  })
  .help()
  .strictCommands().argv
